import {isFeatureEnabled} from 'feature-flags';
import {Avatar, DataStatus, FormSubmitHandler, Separator, Status} from 'platform/components';
import {
  Box,
  Center,
  Clickable,
  Heading,
  Hide,
  HStack,
  Show,
  Space,
  Text,
  VStack,
} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsQuery,
  useReadCodeQuery,
} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {InterestActivityResponseBody, SalesActivityTypeEnumSchema} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetWorkshopInterestQuery,
  useListWorkshopInterestActivitiesQuery,
  WorkshopInterestActivityResponseBody,
} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import {
  ActivityDate,
  ActivitySidebar,
  ActivitySidebarAction,
  ActivitySidebarLink,
  ActivitySidebarV2,
  getActivityIconColor,
  transformActivityItem,
} from '@dms/features/activity-panel';
import i18n from '@dms/i18n';
import {
  CommentCardForm,
  CommentCardItem,
  CommentsCardInput,
  getNaturalPersonFullName,
  handleApiError,
} from '@dms/shared';

import {buildArray, DOT_CHARACTER, parseDate} from 'shared/web';

function toActivityItem(
  item: WorkshopInterestActivityResponseBody
): InterestActivityResponseBody | null {
  const parsedType = SalesActivityTypeEnumSchema.safeParse(item.activityType);

  if (!parsedType.success) {
    return null;
  }

  return {
    id: item.id,
    businessCaseId: item.businessCaseId ?? null,
    businessCaseCode: item.businessCaseCode ?? null,
    businessCaseType: item.businessCaseType ?? null,
    subject: item.subject ?? null,
    activityType: parsedType.data,
    userId: item.userId,
    userName: item.userName,
    timestamp: item.timestamp,
    description: item.description,
    relatedTask: null,
    relatedMessage: null,
    relatedCall: null,
    reasonCloseCodeId: item.reasonCloseCodeId ?? null,
    reasonCloseNote: item.reasonCloseNote ?? null,
    isEditable: item.isEditable ?? false,
  };
}

type ActivityItemProps = {
  item: WorkshopInterestActivityResponseBody;
  showLineToNextItem: boolean;
  interestId: string;
};

function ActivityItem(props: ActivityItemProps) {
  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: props.interestId});

  const {data: sourceCodeData} = useReadCodeQuery(
    {codelistId: 'workshop_interest_source', codeId: workshopInterest?.sourceCodeId ?? ''},
    {skip: !workshopInterest?.sourceCodeId}
  );

  const {data: userData} = useGetUserQuery(
    {id: props.item.userId ?? ''},
    {skip: !props.item.userId || isNotNilOrEmpty(props.item.userName)}
  );

  const activityItem = toActivityItem(props.item);

  if (!activityItem) {
    return null;
  }

  const transformedItem = transformActivityItem(activityItem, undefined, {
    source: sourceCodeData,
  });

  const hasUserName = isNotNilOrEmpty(props.item.userName) || isNotNil(userData);

  if (!transformedItem) {
    return null;
  }

  return (
    <HStack spacing={2}>
      <VStack>
        <Avatar
          color={transformedItem.color}
          icon={transformedItem.icon}
          variant="square"
          iconColor={getActivityIconColor(transformedItem.color)}
        />
        <Hide when={props.showLineToNextItem}>
          <Center width="100%" height="100%">
            <Box height="100%" backgroundColor="general.separator" width="0.125rem" />
          </Center>
        </Hide>
      </VStack>
      <VStack width="100%">
        <Box paddingBottom={4}>
          <VStack spacing={1}>
            <Box paddingBottom={0}>
              <HStack width="100%">
                <VStack width="100%" spacing={1}>
                  <Clickable
                    onClick={transformedItem.onClick}
                    isDisabled={!transformedItem.isEditable}
                  >
                    <Heading size={5}>{transformedItem.title}</Heading>
                  </Clickable>
                  <Show when={transformedItem.interestType || hasUserName}>
                    <Text color="tertiary" size="xSmall">
                      <HStack align="center">
                        <Show when={transformedItem.interestType}>
                          {transformedItem.interestType}
                        </Show>
                        <Show when={transformedItem.interestType && hasUserName}>
                          <Space horizontal={1} />
                          {DOT_CHARACTER}
                          <Space horizontal={1} />
                        </Show>
                        <Show when={hasUserName}>
                          {transformedItem.userName ?? getNaturalPersonFullName(userData)}
                        </Show>
                      </HStack>
                    </Text>
                  </Show>
                  <Show when={isNotNilOrEmpty(transformedItem.description)}>
                    <Box maxWidth={62}>
                      <Text size="xSmall" color="secondary" noWrap>
                        {transformedItem.description}
                      </Text>
                    </Box>
                  </Show>
                  <Show when={isNotNilOrEmpty(transformedItem.links)}>
                    <HStack>
                      {transformedItem.links?.map((link) => (
                        <ActivitySidebarLink key={link.label} {...link} />
                      ))}
                    </HStack>
                  </Show>
                </VStack>
                <ActivityDate timestamp={props.item.timestamp} />
              </HStack>
            </Box>
          </VStack>
        </Box>
      </VStack>
    </HStack>
  );
}

type ActivityListProps = {
  label: string;
  items: WorkshopInterestActivityResponseBody[];
  interestId: string;
  showEmpty?: boolean;
};

function ActivityList(props: ActivityListProps) {
  return (
    <VStack spacing={4} height="auto">
      <Text size="xSmall" color="secondary">
        {props.label}
      </Text>

      <Show when={props.showEmpty && isNilOrEmpty(props.items)}>
        <Status headline={i18n.t('entity.activityPanel.activity.noNextSteps')} />
      </Show>

      <VStack spacing={0}>
        <Show when={props.items.length}>
          {props.items.map((item, index) => (
            <ActivityItem
              key={item.id}
              item={item}
              showLineToNextItem={index === props.items.length - 1}
              interestId={props.interestId}
            />
          ))}
        </Show>
      </VStack>
    </VStack>
  );
}

function ActivityTab(props: {interestId: string}) {
  const {
    data: activities,
    isLoading,
    isError,
  } = useListWorkshopInterestActivitiesQuery(
    {interestId: props.interestId},
    {skip: isNilOrEmpty(props.interestId)}
  );

  const pastActivities =
    activities?.filter((activity) => parseDate(activity.timestamp) < new Date()) ?? [];

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack spacing={4} height="100%">
        <Show when={isNotNilOrEmpty(pastActivities)}>
          <ActivityList
            label={i18n.t('entity.activityPanel.activity.pastActivities')}
            items={pastActivities}
            interestId={props.interestId}
          />
        </Show>
      </VStack>
    </DataStatus>
  );
}

function WorkshopInterestCommentsTab(props: {interestId: string}) {
  const resourceId = EntityResourceIds.workshopInterest;
  const recordId = props.interestId;

  const {
    data: comments,
    isLoading: isFetchingLoading,
    isError,
  } = useGetCommentsQuery({resourceId, recordId});

  const [createComment, {isLoading: isCreateLoading}] = useCreateCommentMutation();
  const [deleteComment, {isLoading: isDeleteLoading}] = useDeleteCommentMutation();

  const isLoading = isFetchingLoading || isCreateLoading || isDeleteLoading;

  const onCommentSubmit: FormSubmitHandler<CommentCardForm> = (values, _, reset) =>
    createComment({
      resourceId,
      recordId,
      createCommentRequestBody: {text: values.comment},
    })
      .unwrap()
      .then(() => {
        reset();
      })
      .catch(handleApiError);

  const handleCommentDelete = (commentId: string) => {
    deleteComment({resourceId, recordId, commentId}).unwrap().catch(handleApiError);
  };

  return (
    <VStack spacing={4} grow={1} minHeight={0}>
      <div css={{flex: 1, overflow: 'auto', minHeight: 0}}>
        <DataStatus
          minHeight={35}
          isLoading={isLoading}
          isError={isError}
          isEmpty={!comments?.length}
          emptyMessage={i18n.t('page.comments.labels.noOneCommented')}
        >
          {comments?.map((comment, index) => (
            <VStack key={comment.id}>
              <CommentCardItem
                comment={comment}
                isDeleteCommentLoading={isLoading}
                isDeleteDisabled={false}
                onDelete={handleCommentDelete}
              />
              <Show when={index !== (comments?.length ?? 0) - 1}>
                <Separator spacing={0} />
              </Show>
            </VStack>
          ))}
        </DataStatus>
      </div>

      <CommentsCardInput onSubmit={onCommentSubmit} isSending={isLoading} />
    </VStack>
  );
}

type WorkshopInterestActivityPanelProps = {
  interestId: string;
};

export function WorkshopInterestActivityPanel(props: WorkshopInterestActivityPanelProps) {
  const actions = buildArray<ActivitySidebarAction>()
    .add({
      id: 'activity',
      label: i18n.t('entity.activityPanel.activity.title'),
      icon: 'sidebar/activity',
      component: <ActivityTab interestId={props.interestId} />,
    })
    .add({
      id: 'comments',
      label: i18n.t('entity.activityPanel.comments.title'),
      icon: 'communication/comment',
      component: <WorkshopInterestCommentsTab interestId={props.interestId} />,
    });

  if (isFeatureEnabled(featureFlags.REDESIGN_ACTIVITY_SIDEBAR)) {
    return <ActivitySidebarV2 actions={actions} />;
  }

  return <ActivitySidebar actions={actions} />;
}
